import { useState, useCallback } from 'react';
import { Loader2, Eye, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function CreditNoteVatAuditPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const runAudit = useCallback(async () => {
    setLoading(true);
    setData(null);
    try {
      const { data: result } = await diagnosticsAPI.creditNoteVatAudit();
      setData(result);
      if (result.summary.mismatch > 0) {
        toast.error(`${result.summary.mismatch} credit note(s) have per-rate VAT mismatches`);
      } else if (result.summary.error > 0) {
        toast.warning(`${result.summary.error} credit note(s) could not be audited`);
      } else {
        toast.success('All credit notes pass per-rate VAT verification');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const verdictBadge = (verdict) => {
    if (verdict === 'ok') return <Badge className="bg-green-100 text-green-800 text-[10px]">OK</Badge>;
    if (verdict === 'mismatch') return <Badge variant="destructive" className="text-[10px]">MISMATCH</Badge>;
    return <Badge variant="outline" className="text-[10px]">ERROR</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Eye className="h-5 w-5 text-blue-500" />
          Credit Note VAT Audit
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Post-hoc verification of the applied credit notes. For each credited
          booking, fetches the original invoice and the credit note from
          Pennylane, groups lines by vat_rate, and compares per-rate totals.
          A green row means the reversal is correct per rate. A red row means
          the credit note collapsed multiple VAT rates into one line (VAT
          mis-reversal). Read-only, no writes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={runAudit} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Eye className="h-4 w-4 mr-2" />
          )}
          Run VAT Audit
        </Button>

        {data && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="rounded-lg border p-3 bg-muted/30">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Total audited</div>
                  <div className="text-lg font-bold tabular-nums">{data.summary.total}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Per-rate OK</div>
                  <div className="text-lg font-bold tabular-nums text-green-600">{data.summary.ok}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">VAT mismatch</div>
                  <div className={`text-lg font-bold tabular-nums ${data.summary.mismatch > 0 ? 'text-red-600' : ''}`}>
                    {data.summary.mismatch}
                  </div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Fetch errors</div>
                  <div className={`text-lg font-bold tabular-nums ${data.summary.error > 0 ? 'text-amber-600' : ''}`}>
                    {data.summary.error}
                  </div>
                </div>
              </div>
            </div>

            {/* Per-booking rows */}
            {data.rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-1 pr-3 font-medium">Verdict</th>
                      <th className="py-1 pr-3 font-medium">Booking</th>
                      <th className="py-1 pr-3 font-medium">Property</th>
                      <th className="py-1 pr-3 font-medium">Original total</th>
                      <th className="py-1 pr-3 font-medium">Credit total</th>
                      <th className="py-1 pr-3 font-medium">Rates</th>
                      <th className="py-1 pr-3 font-medium">Per-rate detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, i) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-1 pr-3">{verdictBadge(row.verdict)}</td>
                        <td className="py-1 pr-3 font-mono">{row.booking_id}</td>
                        <td className="py-1 pr-3">{row.property_name}</td>
                        <td className="py-1 pr-3 tabular-nums">{row.original_total ?? '-'}</td>
                        <td className="py-1 pr-3 tabular-nums">{row.credit_note_total ?? '-'}</td>
                        <td className="py-1 pr-3">
                          {row.original_is_mixed_rate ? (
                            <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700">
                              mixed ({row.original_rate_count})
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px]">uniform</Badge>
                          )}
                        </td>
                        <td className="py-1 pr-3">
                          {row.error ? (
                            <span className="text-red-600">{row.error}</span>
                          ) : row.per_rate_comparison ? (
                            <div className="space-y-0.5">
                              {row.per_rate_comparison.map((pr, j) => (
                                <div key={j} className="flex items-center gap-1">
                                  {pr.match ? (
                                    <CheckCircle2 className="h-3 w-3 text-green-600 flex-shrink-0" />
                                  ) : (
                                    <XCircle className="h-3 w-3 text-red-600 flex-shrink-0" />
                                  )}
                                  <span className="font-mono">{pr.vat_rate}</span>
                                  <span className="text-muted-foreground">
                                    orig={pr.original_total} cn={pr.credit_note_total}
                                  </span>
                                  {!pr.match && (
                                    <span className="text-red-600 font-medium">
                                      delta={pr.net_delta}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data.rows.length === 0 && (
              <p className="text-sm text-muted-foreground italic">
                No credited bookings found.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
